import React, { useState } from 'react'
import {
  Typography,
  Box,
  Stack,
  IconButton,
  Tooltip,
  Fade,
  Skeleton,
} from '@mui/material'
import PushPinIcon from '@mui/icons-material/PushPin'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'

interface Chat {
  chat_uuid: string
  char_uuid: string
  name: string
  description: string
  profile_image: string
  messageCount: number
  is_pinned: boolean
}

interface ChatSectionProps {
  pinnedChats: Chat[]
  unpinnedChats: Chat[]
  isLoading: boolean
  onPin: (chatId: string) => void
  onDelete: (chatId: string) => void
  onClick: (chatId: string) => void
}

export function ChatSection({
  pinnedChats,
  unpinnedChats,
  isLoading,
  onPin,
  onDelete,
  onClick,
}: ChatSectionProps) {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null)

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          justifyContent: { xs: 'center', lg: 'flex-start' },
        }}
      >
        {[...Array(6)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            sx={{
              flex: '1 1 300px',
              maxWidth: 360,
              minWidth: 280,
              height: 140,
              borderRadius: 2,
            }}
          />
        ))}
      </Box>
    )
  }

  const renderGroup = (title: string, chats: Chat[], Icon: React.ElementType, showHeader: boolean = true) => {
    if (chats.length === 0) return null

    return (
      <Box sx={{ width: '100%' }}>
        {showHeader && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <Icon fontSize="small" color="action" />
            <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
              {title} ({chats.length})
            </Typography>
          </Stack>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            justifyContent: { xs: 'center', lg: 'flex-start' },
          }}
        >
          {chats.map((chat) => (
            <Box
              key={chat.chat_uuid}
              onMouseEnter={() => setHoveredCard(chat.chat_uuid)}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={() => onClick(chat.chat_uuid)}
              sx={{
                flex: {
                  xs: '1 1 100%',
                  sm: '1 1 calc(50% - 12px)',
                  md: '1 1 calc(50% - 12px)',
                  lg: '1 1 calc(33.333% - 16px)',
                  xl: '1 1 calc(25% - 18px)',
                },
                maxWidth: { xs: '100%', sm: 400, lg: 380, xl: 320 },
                minWidth: { xs: 280, sm: 300 },
                position: 'relative',
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: 'background.paper',
                  boxShadow: hoveredCard === chat.chat_uuid ? 8 : 1,
                  transform: hoveredCard === chat.chat_uuid ? 'translateY(-4px)' : 'translateY(0)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '1px solid',
                  borderColor: hoveredCard === chat.chat_uuid ? 'primary.main' : 'transparent',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, primary.main, primary.light)',
                    opacity: hoveredCard === chat.chat_uuid ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    component="img"
                    src={chat.profile_image}
                    alt={chat.name}
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2,
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                      transform: hoveredCard === chat.chat_uuid ? 'scale(1.05)' : 'scale(1)',
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h6" 
                      fontWeight="600"
                      sx={{ 
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        transition: 'color 0.2s ease',
                        color: hoveredCard === chat.chat_uuid ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {chat.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 1,
                        lineHeight: 1.5,
                      }}
                    >
                      {chat.description}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.disabled">
                        {chat.messageCount} messages
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              <Fade in={hoveredCard === chat.chat_uuid} timeout={200}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 2,
                  }}
                >
                  <Tooltip title={chat.is_pinned ? 'Unpin' : 'Pin'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        onPin(chat.chat_uuid)
                      }}
                      sx={{
                        backgroundColor: 'background.paper',
                        boxShadow: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <PushPinIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete(chat.chat_uuid)
                      }}
                      sx={{
                        backgroundColor: 'background.paper',
                        boxShadow: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'error.main',
                          color: 'error.contrastText',
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <DeleteSweepIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Fade>
            </Box>
          ))}
        </Box>
      </Box>
    )
  }

  return (
    <Stack spacing={4} sx={{ width: '100%' }}>
      {pinnedChats.length > 0 && (
        <Box>
          <Typography 
            variant="h5" 
            fontWeight="700" 
            color="text.primary" 
            sx={{ mb: 2 }}
          >
            Pinned Chats
          </Typography>
          {renderGroup('Pinned', pinnedChats, PushPinIcon, false)}
        </Box>
      )}
      {unpinnedChats.length > 0 && renderGroup(
        pinnedChats.length > 0 ? 'Recent' : 'All Chats',
        unpinnedChats,
        ChatBubbleOutlineIcon
      )}
    </Stack>
  )
}